import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import { findMediaById, getRelated } from '@/mocks/media';
import { useFavorites } from '@/hooks/useFavorites';

const TYPE_LABEL: Record<string, string> = { movie: '电影', tv: '电视剧' };

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const media = id ? findMediaById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (media) {
      document.title = `${media.title} - 朋友的妈妈在线`;
    }
    return () => {
      document.title = '朋友的妈妈在线_最新电影电视剧在线观看平台';
    };
  }, [media]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2000);
  };

  if (!media) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">影视信息暂时不可用</h1>
        <p className="text-sm text-foreground-500 mt-2">该作品可能已下线或链接失效，欢迎返回首页浏览其他内容。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(media, 6);
  const favorited = isFavorite(media.id);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
    setShareOpen(false);
  };

  const detailRows = [
    { label: '类型', value: media.genres.join(' / ') },
    { label: '地区', value: media.region },
    { label: '年份', value: String(media.year) },
    { label: media.type === 'movie' ? '片长' : '集数', value: media.duration },
    { label: '导演', value: media.director },
    { label: '主演', value: media.cast.join(' / ') },
  ];

  return (
    <div className="w-full">
      <div className="relative w-full bg-primary-950">
        <div className="absolute inset-0 overflow-hidden">
          <img src={media.backdrop ?? media.cover} alt="" aria-hidden="true" className="w-full h-full object-cover object-top opacity-30" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/60"></div>

        <div className="relative z-10 px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-background-300 mb-6">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={media.type === 'movie' ? '/category/movie' : '/category/tv'} className="hover:text-accent-400 transition-colors cursor-pointer">
              {TYPE_LABEL[media.type]}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100 truncate">{media.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-40 sm:w-48 md:w-56 shrink-0 self-start rounded-lg overflow-hidden bg-background-200">
              <img
                src={media.cover}
                alt={`${media.title} 海报`}
                title={`${media.title} - ${media.genres.join(' ')}`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>

            <div className="flex-1 min-w-0">
              <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-accent-500 text-primary-950 text-xs font-bold mb-3">
                {TYPE_LABEL[media.type]}
              </span>
              <h1 className="font-heading font-bold text-background-50 text-2xl md:text-4xl">{media.title}</h1>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
                <span className="flex items-center gap-1 text-accent-400 font-semibold">
                  <i className="ri-star-fill"></i> {media.rating.toFixed(1)}
                </span>
                <span>{media.year}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>{media.region}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>{media.duration}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span className="flex items-center gap-1"><i className="ri-fire-line"></i> {(media.views / 10000).toFixed(0)}万人气</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {media.tags.map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full bg-background-50/10 border border-background-50/20 text-background-100 text-xs">
                    {t}
                  </span>
                ))}
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-2xl">
                {detailRows.map((row) => (
                  <div key={row.label} className="flex gap-2 text-sm">
                    <span className="text-background-400 w-12 shrink-0">{row.label}</span>
                    <span className="text-background-100 min-w-0">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    toggleFavorite(media.id);
                    showToast(favorited ? '已取消收藏' : '已加入收藏，有效期 30 天');
                  }}
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? 'bg-accent-500 text-primary-950 hover:bg-accent-600'
                      : 'bg-background-50/10 border border-background-50/30 text-background-50 hover:bg-background-50/20'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShareOpen((v) => !v)}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-background-50/10 border border-background-50/30 text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-line"></i> 分享
                  </button>
                  {shareOpen && (
                    <div className="absolute left-0 top-full mt-2 z-30 w-48 rounded-lg bg-background-50 border border-background-200 p-2 animate-fade">
                      <button type="button" onClick={copyLink} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 cursor-pointer">
                        <i className="ri-link"></i> 复制链接
                      </button>
                      {[
                        { icon: 'ri-weibo-fill', label: '分享到微博' },
                        { icon: 'ri-wechat-fill', label: '分享到微信' },
                        { icon: 'ri-twitter-x-fill', label: '分享到 X' },
                      ].map((s) => (
                        <button
                          key={s.label}
                          type="button"
                          onClick={() => { showToast('演示分享：' + s.label); setShareOpen(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                        >
                          <i className={s.icon}></i> {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <span className="text-xs text-background-400">本站仅提供影视资讯浏览，不含播放与下载功能</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-4xl">
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">剧情简介</h2>
          <p className="text-sm md:text-base leading-loose text-foreground-700">{media.description}</p>
        </div>

        <div className="mt-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">相关推荐</h2>
              <p className="text-sm text-foreground-500 mt-1">根据题材与标签为你精选的相似作品</p>
            </div>
            <Link to={media.type === 'movie' ? '/category/movie' : '/category/tv'} className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer">
              更多 <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          {related.length === 0 ? (
            <div className="py-12 text-center text-foreground-500 border border-dashed border-background-300 rounded-lg">
              暂无相关推荐
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[70] px-4 py-3 rounded-md bg-primary-950 text-background-50 text-sm animate-fade">
          {toast}
        </div>
      )}
    </div>
  );
}